import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';
import { bindActionCreators } from 'redux';
import { facebookAppId } from 'src/config';
import { get as lodashGet } from 'lodash';
import HvSvg from 'src/components/HvSvg';
import uiDux from 'src/redux/ui';
import EventNames from 'src/lib/eventNames';
import trackingDux from 'src/redux/tracking';
import constants from 'src/constants';
import utils from 'src/utils';
import UserAgent from 'src/utils/UserAgent';
import {
  JobShareLoadingModalContainer,
  JobShareModalContainer,
  IconContainerStyles,
  ModalContent,
  SocialMediaShareContainer,
  CloseIcon,
  Header,
  CopyLink,
  Label,
  CopyLinkTextInput,
  CopyButton,
  ShareWithAFriendContainer,
  EmailAddressInput,
  EmailMessageTextArea,
  SendButton,
} from './styled';

const { EMAIL_STATES } = constants.jobShare;
const { isValidEmailAddress: isValidEmailAddressUtil } = utils.dataValidation;

export const mapStateToProps = (state) => ({
  emailState: state.designPro.jobShare.emailState,
  isIosWebView: state.designPro.app.isIosWebView,
  isJobShareLoading: state.designPro.jobShare.isJobShareLoading,
  isJobShareModalOpen: state.designPro.ui.isJobShareModalOpen,
  jobShareData: state.designPro.jobShare.jobShareData,
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleCloseJobShareClick: uiDux.actions.handleCloseJobShareClick,
      handleSendJobShareEmailClick: uiDux.actions.handleSendJobShareEmailClick,
      handleUpdateJobShareEmailState:
        uiDux.actions.handleUpdateJobShareEmailState,
      track: (event, properties) =>
        trackingDux.actions.track(event, properties),
    },
    dispatch,
  );

export class JobShareModal extends PureComponent {
  static propTypes = {
    emailState: PropTypes.string.isRequired,
    handleCloseJobShareClick: PropTypes.func.isRequired,
    handleSendJobShareEmailClick: PropTypes.func.isRequired,
    handleUpdateJobShareEmailState: PropTypes.func.isRequired,
    isIosWebView: PropTypes.bool.isRequired,
    isJobShareLoading: PropTypes.bool.isRequired,
    isJobShareModalOpen: PropTypes.bool.isRequired,
    jobShareData: PropTypes.object,
    track: PropTypes.func.isRequired,
  };

  @autobind
  handleEmailAddressChange() {
    const { handleUpdateJobShareEmailState, emailState } = this.props;
    const isValidEmailAddress = isValidEmailAddressUtil(
      this.emailAddressInput.value,
    );

    if (emailState === EMAIL_STATES.notReadyToSend.id && isValidEmailAddress) {
      handleUpdateJobShareEmailState(EMAIL_STATES.readyToSend.id);
    } else if (
      emailState === EMAIL_STATES.readyToSend.id &&
      !isValidEmailAddress
    ) {
      handleUpdateJobShareEmailState(EMAIL_STATES.notReadyToSend.id);
    } else if (emailState === EMAIL_STATES.sent.id) {
      if (isValidEmailAddress) {
        handleUpdateJobShareEmailState(EMAIL_STATES.readyToSend.id);
      } else {
        handleUpdateJobShareEmailState(EMAIL_STATES.notReadyToSend.id);
      }
    }
  }

  @autobind
  onCopyButtonClick() {
    const { track, jobShareData } = this.props;

    if (!UserAgent.isIOS()) {
      this.urlInput.select();
    } else {
      const oldContentEditable = this.urlInput.contentEditable;
      const range = document.createRange();
      const oldReadOnly = this.urlInput.readOnly;

      this.urlInput.contentEditable = true;
      this.urlInput.readOnly = false;
      range.selectNodeContents(this.urlInput);

      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(range);

      // A big number, to cover anything that could be inside the element.
      this.urlInput.setSelectionRange(0, 999999);

      this.urlInput.contentEditable = oldContentEditable;
      this.urlInput.readOnly = oldReadOnly;
    }

    document.execCommand('copy');
    track(EventNames.viewer.share.SHARE_FEATURE_CLICK_COPY_LINK, {
      shared_url: jobShareData.url,
    });
  }

  @autobind
  onSendButtonClick() {
    const { handleSendJobShareEmailClick, jobShareData, track } = this.props;

    const email = this.emailAddressInput.value;
    const senderText = this.emailMessageTextArea.value;

    handleSendJobShareEmailClick(email, senderText);
    track(EventNames.viewer.share.SHARE_FEATURE_CLICK_SEND_EMAIL, {
      shared_url: jobShareData.url,
      shared_email: email,
    });
    this.emailAddressInput.value = '';
    this.emailMessageTextArea.value = '';
  }

  @autobind
  openFacebookPopup() {
    const { jobShareData, track } = this.props;

    const url =
      'https://www.facebook.com/dialog/share?' +
      `app_id=${facebookAppId.PRODUCTION}` +
      '&display=popup' +
      `&href=${encodeURI(jobShareData.url)}`;

    window.open(url, '_blank', 'width=550,height=420,menubar=no');
    track(EventNames.viewer.share.SHARE_FEATURE_CLICK_FACEBOOK, {
      shared_url: jobShareData.url,
    });
  }

  @autobind
  openTwitterPopup() {
    const { jobShareData, track } = this.props;

    const { street_address: address, url } = jobShareData;
    const composedUrl =
      'https://twitter.com/intent/tweet?' +
      `url=${url}` +
      '&via=HOVER3D' +
      `&text=${address}`;

    window.open(composedUrl, '_blank', 'width=550,height=420,menubar=no');
    track(EventNames.viewer.share.SHARE_FEATURE_CLICK_TWITTER, {
      shared_url: jobShareData.url,
    });
  }

  renderSendButton() {
    const { emailState } = this.props;
    const textToDisplay = lodashGet(
      EMAIL_STATES,
      `[${emailState}].textToDisplay`,
    );

    return (
      <SendButton
        onClick={this.onSendButtonClick}
        emailState={emailState}
        disabled={emailState !== EMAIL_STATES.readyToSend.id}
        data-selenium="jobShareWithFriendSendButton"
      >
        {textToDisplay}
      </SendButton>
    );
  }

  renderHeader() {
    const { handleCloseJobShareClick, jobShareData } = this.props;

    return (
      <>
        <CloseIcon onClick={handleCloseJobShareClick} role="button">
          <HvSvg src={require('src/assets/images/ic-plus.svg')} />
        </CloseIcon>
        <Header>Share {jobShareData.street_address}</Header>
      </>
    );
  }

  renderCopyLink() {
    const { jobShareData } = this.props;

    return (
      <CopyLink>
        <Label>Link</Label>
        <CopyLinkTextInput
          type="text"
          value={jobShareData.url}
          innerRef={(c) => {
            this.urlInput = c;
          }}
          data-selenium="jobShareCopyLinkInput"
          readOnly
        />
        <CopyButton
          onClick={this.onCopyButtonClick}
          data-selenium="jobShareCopyLinkButton"
        >
          COPY
        </CopyButton>
      </CopyLink>
    );
  }

  renderShareWithAFriend() {
    return (
      <ShareWithAFriendContainer>
        <Label>Share with a friend</Label>
        <EmailAddressInput
          type="email"
          placeholder="Enter email address"
          onChange={this.handleEmailAddressChange}
          innerRef={(c) => {
            this.emailAddressInput = c;
          }}
          data-selenium="jobShareWithFriendEmailInput"
        />
        <EmailMessageTextArea
          placeholder="Add your message... (128 character max)"
          innerRef={(c) => {
            this.emailMessageTextArea = c;
          }}
          data-selenium="jobShareWithFriendMessageInput"
        />
        {this.renderSendButton()}
      </ShareWithAFriendContainer>
    );
  }

  renderSocialMediaShare() {
    if (UserAgent.isInNativeWebView()) {
      return '';
    }
    return (
      <SocialMediaShareContainer>
        <IconContainerStyles
          role="button"
          onClick={this.openFacebookPopup}
          marginLeft="32"
          size="28"
          data-selenium="jobShareFacebookButton"
        >
          <HvSvg
            src={require('src/assets/icons/facebook.svg')}
            disabled={false}
          />
        </IconContainerStyles>

        <IconContainerStyles
          role="button"
          onClick={this.openTwitterPopup}
          marginLeft="20"
          size="28"
          data-selenium="jobShareTwitterButton"
        >
          <HvSvg
            src={require('src/assets/icons/twitter.svg')}
            disabled={false}
          />
        </IconContainerStyles>
      </SocialMediaShareContainer>
    );
  }

  renderLoadingModal() {
    const { isJobShareModalOpen } = this.props;

    return (
      <JobShareLoadingModalContainer isOpen={isJobShareModalOpen}>
        <span>Loading... Please wait</span>
      </JobShareLoadingModalContainer>
    );
  }

  renderJobShareModal() {
    const { isIosWebView, isJobShareModalOpen } = this.props;

    return (
      <JobShareModalContainer isOpen={isJobShareModalOpen}>
        <ModalContent isIOS={isIosWebView}>
          {this.renderHeader()}
          {this.renderCopyLink()}
          {this.renderShareWithAFriend()}
        </ModalContent>
        {this.renderSocialMediaShare()}
      </JobShareModalContainer>
    );
  }

  render() {
    const { isJobShareLoading, jobShareData } = this.props;

    return isJobShareLoading || !jobShareData
      ? this.renderLoadingModal()
      : this.renderJobShareModal();
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(JobShareModal);
